import React, { useCallback, useEffect, useState, useRef } from 'react';
import '@/assets/styles/views/messages/messages.styles.scss';
import ChatList from './components/ChatList';
import MessageContent from './components/MessageContent';
import MessageInput from './components/MessageInput';
import Avatar from './components/Avatar';
import { Message, SenderType, Student, Support, SupportTopic, User, UserType } from './messages.types';
import MessagesPlaceholder from '@/assets/svg/messages-placeholder.svg';
import { useApi } from '@/api/useApi.tsx';
import { useDebounce, useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useInfiniteQueryProvider } from '@/hooks/use-infinity-query-provider.tsx';
import { StudentExpertChatMessageCreateRequest } from '@dopingtech/live_expert_bff_api_ts';
import TopicListNormal from '@/screens/messages/components/TopicListNormal.tsx';
import dayjs from 'dayjs';
import MessageContentSupport from '@/screens/messages/components/MessageContentSupport.tsx';
import clsx from 'clsx';
import { ExpertSupervisorMessageCreateRequest, SupportTicketMessageResponseSenderTypeEnum, TicketStatusUpdateRequestStatusEnum } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import NewTopicModal from '@/screens/messages/components/NewTopicModal.tsx';
import DhAvatar from '@/assets/svg/dh-avatar.svg';
import { isString } from '@/utility';
import NewSupportTicketModal from '@/screens/messages/components/NewSupportTicketModal.tsx';
import DopingIcon from '@/assets/images/doping-icon.png';
import { KY_INSTANT_ID, SERVICES_COACHING_LIST, SERVICES_GOT_IT_LECTURE_LIST } from '@/constants';
import { useLocation, useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';
import { useSizes } from '@/utility/responsive.ts';
import { useQueryClient } from 'react-query';
import { useFeatureFlag } from 'configcat-react';
import ExceptionManager from '@/managers/exception-manager.tsx';
import ParentPlaceholderIcon from '@/assets/images/parent-placeholder.png';
import ParentPlaceholder from '@/screens/messages/components/ParentPlaceholder.tsx';

const currentUser: User = {
	id: 1,
	name: 'Ergin',
	surname: 'Özbedir',
	avatar: 'https://randomuser.me/api/portraits/men/43.jpg',
	type: UserType.GOT_IT,
	title: 'Öğretmen'
};

const TECHNICAL = 'technical';
const APPROVED = 'APPROVED';

const MessagesScreen: React.FC = () => {
	const { state } = useLocation();
	const initialStudentId = state?.studentId ? parseInt(state?.studentId) : undefined;

	const [userType, setUserType] = useState<UserType | null>(null);
	const [selectedItemId, setSelectedItemId] = useState<number | string | null>(null);
	const [isSupport, setIsSupport] = useState<boolean>(false);
	const [selectedTopicId, setSelectedTopicId] = useState<number | null>(null);
	const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
	const [showReplyInput, setShowReplyInput] = useState<boolean>(false);
	const [messages, setMessages] = useState<Message[]>([]);
	const [showNewTopicModal, setShowNewTopicModal] = useState<boolean>(false);
	const [showNewTicketModal, setShowNewTicketModal] = useState<boolean>(false);
	const [selectedTicket, setSelectedTicket] = useState<any>(null);
	const [initialSelectionDone, setInitialSelectionDone] = useState<boolean>(false);
	const [searchQuery, setSearchQuery] = useState('');
	const debouncedSearchQuery = useDebounce(searchQuery, 500);
	const [cachedSelectedStudent, setCachedSelectedStudent] = useState<Student | null>(null);
	const { value: isSupervisorActive } = useFeatureFlag('isSupervisorActive', true);
	const { value: parentChatEnabled } = useFeatureFlag('parentchatenabled', false);

	const [hasRelatedStudent, setHasRelatedStudent] = useState<boolean>(false);

	const [selectedParent, setSelectedParent] = useState<any>(null);
	const [selectedParentId, setSelectedParentId] = useState<number | null>(null);
	const [showParentDropdown, setShowParentDropdown] = useState(false);
	const [showMobileParentModal, setShowMobileParentModal] = useState(false);
	const [showParentPlaceholder, setShowParentPlaceholder] = useState(false);

	const messageReadRef = useRef<{
		studentId?: number;
		parentId?: number;
		lastStudentMessageDate?: string;
		lastParentMessageDate?: string;
	}>({});

	const { isMobile } = useSizes();
	const navigate = useNavigate();
	const [keyboardHeight, setKeyboardHeight] = useState(0);
	const queryClient = useQueryClient();

	useEffect(() => {
		if (!isMobile || !window.visualViewport) return;

		const handleViewportResize = () => {
			const overlap = window.innerHeight - (window.visualViewport?.height ?? window.innerHeight);
			setKeyboardHeight(overlap > 50 ? Math.round(overlap) : 0);
		};

		window.visualViewport.addEventListener('resize', handleViewportResize);
		handleViewportResize();

		return () => window.visualViewport?.removeEventListener('resize', handleViewportResize);
	}, [isMobile]);

	const { StudentExpertChatControllerApi, ExpertSupervisorChatControllerApi, SupervisorControllerApi, TechnicalSupportControllerApi, ExpertControllerApi, ExpertParentChatControllerApi, SupportTicketControllerApi } = useApi();

	useQueryProvider([QUERY_KEYS.getLoggedInExpertDetailInfo], () => ExpertControllerApi.getLoggedInExpertDetailInfo(), {
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false,
		onSuccess: response => {
			if (response?.serviceList?.length && response?.serviceList?.length > 0) {
				const isInstantCoach = response.serviceList.find(x => {
					if (x.expertServiceStatus === APPROVED && x?.liveServiceTypeId && x?.liveServiceTypeId === KY_INSTANT_ID) {
						return true;
					} else return false;
				});

				const isAppointmentCoach = response.serviceList.find(x => {
					if (x.expertServiceStatus === APPROVED && x?.liveServiceTypeId && Object.keys(SERVICES_COACHING_LIST).includes(x?.liveServiceTypeId?.toString())) {
						return true;
					} else return false;
				});

				const isOneOnOneLecture = response.serviceList.find(x => {
					if (x.expertServiceStatus === APPROVED && x?.liveServiceTypeId && Object.keys(SERVICES_GOT_IT_LECTURE_LIST).includes(x?.liveServiceTypeId?.toString())) {
						return true;
					} else return false;
				});

				if (isAppointmentCoach || isOneOnOneLecture) {
					setUserType(UserType.KY_APPOINTMENT);
				} else if (isInstantCoach) {
					setUserType(UserType.KY_INSTANT);
				} else {
					setUserType(UserType.GOT_IT);
				}
			}
		}
	});

	const {
		data: studentsData,
		fetchNextPage: fetchNextStudentsPage,
		hasNextPage: hasNextStudentsPage,
		isFetchingNextPage: isFetchingNextStudentsPage,
		refetch: refetchStudentsData
	} = useInfiniteQueryProvider(
		[QUERY_KEYS.findStudentExpertChats, debouncedSearchQuery],
		({ pageParam = 0 }) => {
			const sort = ['lastMessageDate,DESC'];
			return StudentExpertChatControllerApi.findStudentExpertChats(debouncedSearchQuery || undefined, pageParam as number, 15, sort);
		},
		{
			enabled: userType === UserType.KY_APPOINTMENT,
			getNextPageParam: lastPage => {
				const pageData = lastPage?.data?.data;

				if (pageData && !pageData.last) {
					return Number(pageData.number) + 1;
				}
				return undefined;
			}
		}
	);

	const { data: supportData, refetch: refetchSupportData } = useQueryProvider([QUERY_KEYS.getSupervisorOfLoggedInExpert], () => SupervisorControllerApi.getSupervisorOfLoggedInExpert(), {
		enabled: userType === UserType.KY_APPOINTMENT || userType === UserType.KY_INSTANT,
		select: ({ data }) => data?.data
	});

	const { data: supportCount, refetch: refetchSupportCount } = useQueryProvider([QUERY_KEYS.findUnreadSupportMessageCount], () => TechnicalSupportControllerApi.findUnreadMessageCount(), {
		enabled: userType === UserType.KY_APPOINTMENT || userType === UserType.KY_INSTANT,
		select: ({ data }) => data?.data,
		refetchOnWindowFocus: false
	});

	const {
		data: studentChatData,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		refetch: refetchChatData
	} = useInfiniteQueryProvider(
		[QUERY_KEYS.findStudentExpertChatMessages, selectedItemId?.toString() || ''],
		({ pageParam }) => StudentExpertChatControllerApi.findStudentExpertChatMessages(selectedItemId as number, pageParam as string),
		{
			enabled: selectedItemId !== null && !isSupport && !selectedParentId,
			getNextPageParam: lastPage => {
				return lastPage?.data?.data?.hasNext ? lastPage?.data?.data?.nextPageKey : undefined;
			}
		}
	);

	const {
		data: parentChatData,
		fetchNextPage: fetchNextPageParent,
		hasNextPage: hasNextPageParent,
		isFetchingNextPage: isFetchingNextPageParent,
		refetch: refetchParentChatData
	} = useInfiniteQueryProvider(
		[QUERY_KEYS.findExpertParentChatMessages, selectedParentId?.toString() || '', selectedItemId?.toString() || ''],
		({ pageParam }) => ExpertParentChatControllerApi.findExpertParentChatMessages(selectedParentId as number, selectedItemId as number, 10, pageParam as string),
		{
			enabled: selectedParentId !== null && !isSupport && selectedItemId !== null,
			getNextPageParam: lastPage => {
				return lastPage?.data?.data?.hasNext ? lastPage?.data?.data?.nextPageKey : undefined;
			}
		}
	);

	const { data: parentsData, refetch: refetchParentsData } = useQueryProvider(
		[QUERY_KEYS.findExpertParentChats, selectedItemId?.toString() || ''],
		() => ExpertParentChatControllerApi.findExpertParentChats(selectedItemId as number),
		{
			enabled: selectedItemId !== null && !isSupport && parentChatEnabled,
			select: ({ data }) => data?.data
		}
	);

	const {
		data: supportTicketsData,
		refetch: refetchSupportTicketsData,
		fetchNextPage: fetchNextSupportTicketsPage,
		hasNextPage: hasNextPageSupportTickets,
		isFetchingNextPage: isFetchingNextPageSupportTickets
	} = useInfiniteQueryProvider(
		[QUERY_KEYS.getSupportTickets, hasRelatedStudent],
		({ pageParam = 0 }) =>
			SupportTicketControllerApi.getSupportTickets(undefined, 'EXPERT', hasRelatedStudent, pageParam as number, 8, ['createdAt,DESC']).then(x => ({
				data: {
					data: {
						items: x?.data?.data?.content,
						hasNext: !x?.data?.data?.last,
						nextPageKey: typeof x?.data?.data?.number === 'number' ? x?.data?.data?.number + 1 : undefined
					}
				}
			})),
		{
			enabled: isSupport && selectedItemId === TECHNICAL,
			getNextPageParam: lastPage => {
				return lastPage?.data?.data?.hasNext ? lastPage?.data?.data?.nextPageKey : undefined;
			}
		}
	);

	const {
		data: supervisorTopicData,
		refetch: refetchSupervisorTopicData,
		fetchNextPage: fetchNextSupervisorTopicPage,
		hasNextPage: hasNextPageSupervisorTopic,
		isFetchingNextPage: isFetchingNextPageSupervisorTopic
	} = useInfiniteQueryProvider(
		[QUERY_KEYS.findAllChats, selectedItemId?.toString() || ''],
		({ pageParam }) => ExpertSupervisorChatControllerApi.findAllChats(pageParam as string, 8),
		{
			enabled: isSupport && selectedItemId !== TECHNICAL,
			getNextPageParam: lastPage => {
				return lastPage?.data?.data?.hasNext ? lastPage?.data?.data?.nextPageKey : undefined;
			}
		}
	);

	const {
		data: supportChatData,
		fetchNextPage: fetchNextPageSupports,
		hasNextPage: hasNextPageSupports,
		isFetchingNextPage: isFetchingNextPageSupports,
		refetch: refetchSupportChatData
	} = useInfiniteQueryProvider(
		[QUERY_KEYS.findChatMessages, selectedTopicId?.toString() || ''],
		({ pageParam }) => ExpertSupervisorChatControllerApi.findChatMessages(selectedTopicId as unknown as string, pageParam as string),
		{
			enabled: selectedItemId !== null && isSupport && selectedTopicId !== null && !selectedTopicId.toString().includes(TECHNICAL),
			getNextPageParam: lastPage => {
				return lastPage?.data?.data?.hasNext ? lastPage?.data?.data?.nextPageKey : undefined;
			}
		}
	);

	const { data: ticketMessages } = useQueryProvider(
		[QUERY_KEYS.getSupportTicketMessages, selectedTicket?.id ?? ''],
		() => SupportTicketControllerApi.getSupportTicketMessages(selectedTicket!.id as number, 0, 50, ['createdAt,ASC']),
		{
			select: ({ data }) => data?.data?.messages?.content,
			enabled: !!selectedTicket?.id,
			refetchOnWindowFocus: false
		}
	);

	const sendMessageMutation = useMutationProvider((messageRequest: StudentExpertChatMessageCreateRequest) => StudentExpertChatControllerApi.sendMessage(messageRequest), {
		onSuccess: () => {
			refetchChatData();
		},
		onError: error => {
			console.error('Error sending message:', error);
		}
	});

	const sendParentMessageMutation = useMutationProvider((messageRequest: { parentId: number; studentId: number; message: string }) => ExpertParentChatControllerApi.sendMessage2(messageRequest), {
		onSuccess: () => {
			refetchParentChatData();
			refetchParentsData();
		},
		onError: error => {
			console.error('Error sending parent message:', error);
		}
	});

	const readParentMessageMutation = useMutationProvider(
		({ parentId, studentId }: { parentId: number; studentId: number }) => ExpertParentChatControllerApi.resetUnreadMessagesCount2(parentId, studentId),
		{
			onSuccess: () => {
				queryClient.refetchQueries([QUERY_KEYS.findStudentExpertChats]);
				refetchParentsData();
				queryClient.refetchQueries([QUERY_KEYS.findExpertUnreadChatCount]);
			}
		}
	);

	const readMessageMutation = useMutationProvider((studentId: number) => StudentExpertChatControllerApi.resetUnreadMessagesCount(studentId), {
		onSuccess: () => {
			queryClient.refetchQueries([QUERY_KEYS.findStudentExpertChats]);
			queryClient.refetchQueries([QUERY_KEYS.findExpertUnreadChatCount]);
		}
	});

	const readSupportMessageMutation = useMutationProvider((chatId: string) => ExpertSupervisorChatControllerApi.resetUnreadMessagesCount1(chatId), {
		onSuccess: () => {
			refetchSupervisorTopicData();
			refetchSupportData();
			queryClient.refetchQueries([QUERY_KEYS.findExpertUnreadChatCount]);
		}
	});

	const readSupportTechnicalMessageMutation = useMutationProvider((chatId: number) => TechnicalSupportControllerApi.resetUnreadMessageCount(chatId), {
		onSuccess: () => {
			refetchSupportTicketsData();
			refetchSupportCount();
		}
	});

	const sendMessageSupportMutation = useMutationProvider((messageRequest: ExpertSupervisorMessageCreateRequest) => ExpertSupervisorChatControllerApi.sendMessage1(messageRequest), {
		onSuccess: () => {
			refetchSupportChatData();
		},
		onError: ExceptionManager.errorHandler
	});

	const updateTicketStatusMutation = useMutationProvider(
		({ ticketId, status }: { ticketId: number; status: TicketStatusUpdateRequestStatusEnum }) => SupportTicketControllerApi.updateSupportTicketStatus(ticketId, { status }),
		{
			onSuccess: () => {
				queryClient.invalidateQueries([QUERY_KEYS.getSupportTickets]);
			},
			onError: ExceptionManager.errorHandler
		}
	);

	const sendTicketMessageMutation = useMutationProvider(
		({ ticketId, content }: { ticketId: number; content: string }) =>
			SupportTicketControllerApi.createTicketMessage(ticketId, { content }),
		{
			onSuccess: (_data, { ticketId }) => {
				queryClient.invalidateQueries([QUERY_KEYS.getSupportTicketMessages, selectedTicket?.id ?? '']);
				updateTicketStatusMutation.mutate({ ticketId, status: TicketStatusUpdateRequestStatusEnum.WaitingAgent });
			},
			onError: ExceptionManager.errorHandler
		}
	);

	const students: Student[] = React.useMemo(() => {
		const pages = studentsData?.pages ?? [];
		const raw = pages.flatMap(p => p?.data?.data?.content ?? []);
		const studentsList = raw.map(student => ({
			id: student.studentId,
			name: student.studentName,
			surname: student.studentSurname,
			avatar: student?.studentAvatarUrl || 'https://randomuser.me/api/portraits/lego/1.jpg',
			grade: student.studentGradeName,
			lastMessageDate: student.lastMessageDate,
			unreadCount: student.unreadMessageCount || 0,
			isTopRanked: student?.isTopRanked
		}));

		if (debouncedSearchQuery && selectedItemId && !isSupport && cachedSelectedStudent) {
			const selectedStudentExists = studentsList.find(s => s.id === selectedItemId);

			if (!selectedStudentExists) {
				return [cachedSelectedStudent, ...studentsList];
			}
		}

		return studentsList;
	}, [studentsData, debouncedSearchQuery, selectedItemId, isSupport, cachedSelectedStudent]);

	const supportTopics: SupportTopic[] = React.useMemo(() => {
		if (selectedItemId === TECHNICAL) {
			if (!supportTicketsData) return [];
			return supportTicketsData.pages
				.flatMap(page => page?.data?.data?.items || [])
				.map(ticket => ({
					id: TECHNICAL + ticket?.id?.toString(),
					title: ticket?.subject,
					description: ticket?.lastMessage,
					date: dayjs(ticket?.lastMessageDate).format('DD MMMM YYYY'),
					icon: 'icons-dialog',
					isRead: true,
					unreadCount: 0,
					completed: ticket?.status === 'RESOLVED' || ticket?.status === 'CLOSED'
				}));
		}

		if (!supervisorTopicData) return [];
		return supervisorTopicData.pages
			.flatMap(page => page?.data?.data?.items || [])
			.map(topic => ({
				id: topic.chatId || TECHNICAL + topic?.id?.toString(),
				title: topic.topicName,
				description: topic.lastMessage,
				date: dayjs(topic.lastMessageDate).format('DD MMMM YYYY'),
				icon: 'icons-dialog',
				isRead: topic.unreadMessageCount === 0,
				unreadCount: topic.unreadMessageCount,
				completed: topic.completed || false
			}));
	}, [selectedItemId, supportTicketsData, supervisorTopicData]);

	const supports: Support[] = React.useMemo(() => {
		const technicalSupport = {
			id: 'technical',
			name: 'Doping',
			surname: 'Hafıza',
			avatar: DhAvatar,
			title: 'Teknik Destek',
			unreadCount: supportCount || 0
		};

		if (!supportData || !isSupervisorActive) return [technicalSupport];
		return [
			{
				id: supportData.id,
				name: supportData.name,
				surname: supportData.surname,
				avatar: supportData.avatarUrl,
				title: 'Supervisor',
				unreadCount: supportData.unreadMessageCount
			},
			technicalSupport
		];
	}, [supportData, supportCount, isSupervisorActive]);

	const lastStudentMessageDate = React.useMemo(() => {
		if (!studentChatData?.pages?.length) return null;
		const firstPage = studentChatData.pages[0];
		const items = firstPage?.data?.data?.items || [];
		if (!items.length) return null;
		const sortedItems = [...items].sort((a, b) => new Date(b.messageDate as unknown as Date).getTime() - new Date(a.messageDate as unknown as Date).getTime());
		return sortedItems[0]?.messageDate?.toString() || null;
	}, [studentChatData]);

	const lastParentMessageDate = React.useMemo(() => {
		if (!parentChatData?.pages?.length) return null;
		const firstPage = parentChatData.pages[0];
		const items = firstPage?.data?.data?.items || [];
		if (!items.length) return null;
		const sortedItems = [...items].sort((a, b) => new Date(b.messageDate as unknown as Date).getTime() - new Date(a.messageDate as unknown as Date).getTime());
		return sortedItems[0]?.messageDate?.toString() || null;
	}, [parentChatData]);

	useEffect(() => {
		if (!cachedSelectedStudent) return;

		const updated = students.find(s => s.id === cachedSelectedStudent.id);

		if (updated) {
			if (
				updated.unreadCount !== cachedSelectedStudent.unreadCount ||
				updated.name !== cachedSelectedStudent.name ||
				updated.surname !== cachedSelectedStudent.surname ||
				updated.avatar !== cachedSelectedStudent.avatar ||
				updated.grade !== cachedSelectedStudent.grade
			) {
				setCachedSelectedStudent(updated);
			}
		}
	}, [students, cachedSelectedStudent]);

	useEffect(() => {
		if (lastStudentMessageDate && selectedItemId && !isSupport && !selectedParentId) {
			const selectedStudent = students.find(s => s?.id === selectedItemId);
			if (selectedStudent?.unreadCount && (messageReadRef.current.studentId !== selectedItemId || messageReadRef.current.lastStudentMessageDate !== lastStudentMessageDate)) {
				messageReadRef.current.studentId = selectedItemId as number;
				messageReadRef.current.lastStudentMessageDate = lastStudentMessageDate;
				readMessageMutation.mutate(selectedItemId as number);
			}
		}
	}, [lastStudentMessageDate, selectedItemId, isSupport, selectedParentId, students]);

	useEffect(() => {
		if (lastParentMessageDate && selectedParentId && selectedItemId && !isSupport) {
			const selectedParentData = parentsData?.find(p => p?.parentId === selectedParentId);
			if (selectedParentData?.unreadMessageCount && (messageReadRef.current.parentId !== selectedParentId || messageReadRef.current.lastParentMessageDate !== lastParentMessageDate)) {
				messageReadRef.current.parentId = selectedParentId;
				messageReadRef.current.lastParentMessageDate = lastParentMessageDate;
				readParentMessageMutation.mutate({
					parentId: selectedParentId,
					studentId: selectedItemId as number
				});
			}
		}
	}, [lastParentMessageDate, selectedParentId, selectedItemId, isSupport, parentsData]);

	useEffect(() => {
		if (supportChatData && selectedItemId && isSupport && selectedTopicId && !selectedTopicId.toString().includes(TECHNICAL)) {
			const selectedTopic = supportTopics?.find(s => s?.id === selectedItemId);
			if (selectedTopic?.unreadCount) {
				readSupportMessageMutation.mutate(selectedItemId as string);
			}
		}
	}, [supportChatData, selectedItemId, isSupport, selectedTopicId, supportTopics]);

	useEffect(() => {
		messageReadRef.current = {};
	}, [selectedItemId, selectedParentId]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as HTMLElement;
			if (!target.closest('.parent-item')) {
				setShowParentDropdown(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	useEffect(() => {
		setFilteredStudents(students);
	}, [students]);

	useEffect(() => {
		if (userType === UserType.GOT_IT) {
			setSelectedItemId('technical');
			setIsSupport(true);
		} else if (!initialStudentId) {
			setSelectedItemId(null);
		}
		setSelectedTopicId(null);
		setShowReplyInput(false);
	}, [userType, initialStudentId]);

	useEffect(() => {
		if (selectedItemId && selectedParentId && parentChatData && !isSupport) {
			const apiMessages = parentChatData.pages
				.flatMap(page => page?.data?.data?.items || [])
				.sort((a, b) => new Date(a.messageDate as unknown as Date).getTime() - new Date(b.messageDate as unknown as Date).getTime());
			const transformedMessages = apiMessages.map((apiMessage, index) => {
				const selectedParentData = parentsData?.find(p => p.parentId === selectedParentId);

				return {
					id: index,
					content: apiMessage.message,
					date: new Date(apiMessage.messageDate as unknown as Date).toISOString().split('T')[0],
					time: new Date(apiMessage.messageDate as unknown as Date).toLocaleTimeString('tr-TR', {
						hour: '2-digit',
						minute: '2-digit'
					}),
                    senderType: apiMessage.senderType,
					sender: apiMessage.writtenBy === 'EXPERT' ? SenderType.USER : SenderType.STUDENT,
					senderUser:
						apiMessage.writtenBy === 'PARENT'
							? {
									id: selectedParentId,
									name: selectedParentData?.parentFullName?.split(' ')[0] || '',
									surname: selectedParentData?.parentFullName?.split(' ')[1] || '',
									avatar: ''
								}
							: undefined,
					isRead: true
				};
			});

			setMessages(transformedMessages);
		} else if (selectedItemId && studentChatData && !isSupport && !selectedParentId) {
			const apiMessages = studentChatData.pages
				.flatMap(page => page?.data?.data?.items || [])
				.sort((a, b) => new Date(a.messageDate as unknown as Date).getTime() - new Date(b.messageDate as unknown as Date).getTime());

			const transformedMessages = apiMessages.map((apiMessage, index) => {
				const selectedStudent = students.find(s => s.id === selectedItemId);

				return {
					id: index,
					content: apiMessage.message,
					date: new Date(apiMessage.messageDate as unknown as Date).toISOString().split('T')[0],
					time: new Date(apiMessage.messageDate as unknown as Date).toLocaleTimeString('tr-TR', {
						hour: '2-digit',
						minute: '2-digit'
					}),
					sender: apiMessage.writtenBy === 'EXPERT' ? SenderType.USER : SenderType.STUDENT,
					senderUser:
						apiMessage.writtenBy === 'STUDENT'
							? {
									id: selectedItemId,
									name: selectedStudent?.name || '',
									surname: selectedStudent?.surname || '',
									avatar: selectedStudent?.avatar || '',
									grade: selectedStudent?.grade || ''
								}
							: undefined,
					isRead: true
				};
			});

			setMessages(transformedMessages);
		} else if (selectedItemId && isString(selectedTopicId) && isTechnicalTopic && isSupport) {
			const msgs = (ticketMessages ?? []).map((msg, index) => {
				const isExpert = msg.senderType === SupportTicketMessageResponseSenderTypeEnum.Expert;
				return {
					id: index,
					content: msg.content,
					date: dayjs(msg.createdAt).format('YYYY-MM-DD'),
					time: dayjs(msg.createdAt).format('HH:mm'),
					sender: isExpert ? SenderType.USER : SenderType.STUDENT,
                    senderType: msg.senderType,
					senderUser: isExpert
						? undefined
						: { id: 0, name: 'Teknik', surname: 'Destek', avatar: DhAvatar },
					isRead: msg.isRead ?? true
				};
			});
			setMessages(msgs);
		} else if (selectedItemId && selectedTopicId && supportChatData && isSupport) {
			const apiMessages = supportChatData.pages
				.flatMap(page => page?.data?.data?.items || [])
				.sort((a, b) => new Date(a.messageDate as unknown as Date).getTime() - new Date(b.messageDate as unknown as Date).getTime());

			const transformedMessages = apiMessages.map((apiMessage, index) => {
				const selectedSupport = supports[0];

				return {
					id: index,
					content: apiMessage.message,
					date: new Date(apiMessage.messageDate as unknown as Date).toISOString().split('T')[0],
					time: new Date(apiMessage.messageDate as unknown as Date).toLocaleTimeString('tr-TR', {
						hour: '2-digit',
						minute: '2-digit'
					}),
					sender: apiMessage.writtenBy === 'EXPERT' ? SenderType.USER : SenderType.STUDENT,
					senderUser:
						apiMessage.writtenBy === 'SUPERVISOR'
							? {
									id: selectedItemId,
									name: selectedSupport?.name || '',
									surname: selectedSupport?.surname || '',
									avatar: selectedSupport?.avatar || ''
								}
							: undefined,
					isRead: true
				};
			});

			setMessages(transformedMessages);
		} else {
			setMessages([]);
		}
	}, [selectedItemId, selectedParentId, studentChatData, parentChatData, students, supportChatData, selectedTopicId, parentsData, ticketMessages, supports, isSupport]);

	useEffect(() => {
		if (!initialSelectionDone && students?.length > 0 && initialStudentId) {
			setSelectedItemId(initialStudentId);
			setIsSupport(false);
			setSelectedTopicId(null);
			setInitialSelectionDone(true);
		}
	}, [students, initialStudentId, initialSelectionDone]);

	const isTechnical = selectedItemId === TECHNICAL;
	const isTechnicalTopic = selectedTopicId?.toString().includes(TECHNICAL) ?? false;
	const fetchNextSupportTopicData = isTechnical ? fetchNextSupportTicketsPage : fetchNextSupervisorTopicPage;
	const hasNextPageSupportTopicData = isTechnical ? hasNextPageSupportTickets : hasNextPageSupervisorTopic;
	const isFetchingNextPageSupportTopicData = isTechnical ? isFetchingNextPageSupportTickets : isFetchingNextPageSupervisorTopic;

	const handleLoadMoreStudents = useCallback(() => {
		if (hasNextStudentsPage && !isFetchingNextStudentsPage) {
			fetchNextStudentsPage();
		}
	}, [hasNextStudentsPage, isFetchingNextStudentsPage, fetchNextStudentsPage]);

	const handleLoadMore = useCallback(() => {
		if (selectedParentId && hasNextPageParent && !isFetchingNextPageParent) {
			fetchNextPageParent();
		} else if (hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	}, [selectedParentId, hasNextPageParent, isFetchingNextPageParent, fetchNextPageParent, fetchNextPage, hasNextPage, isFetchingNextPage]);

	const handleLoadMoreSupports = useCallback(() => {
		if (hasNextPageSupports && !isFetchingNextPageSupports) {
			fetchNextPageSupports();
		}
	}, [fetchNextPageSupports, hasNextPageSupports, isFetchingNextPageSupports]);

	const handleSearch = (query: string) => {
		setSearchQuery(query);
	};

	const handleSelectItem = useCallback(
		(id: number, support: boolean) => {
			setSelectedItemId(id);
			setIsSupport(support);
			setSelectedTopicId(null);
			setSelectedParentId(null);
			setSelectedParent(null);
			setShowParentPlaceholder(false);
			setHasRelatedStudent(false);

			if (!support) {
				const student = students.find(s => s.id === id);
				if (student) {
					setCachedSelectedStudent(student);
				}
			}
		},
		[students]
	);

	useEffect(() => {
		if (parentsData && parentsData.length === 1) {
			const singleParent = parentsData[0];
			setSelectedParent(singleParent);
		} else if (parentsData && parentsData.length > 1 && selectedParent !== null) {
			setSelectedParent(parentsData.find(p => p.parentId === selectedParentId) || null);
		}
	}, [parentsData]);

	useEffect(() => {
		if (debouncedSearchQuery) {
			if (students.length === 1 && students[0].id === selectedItemId && cachedSelectedStudent) {
				setFilteredStudents([
					{
						...cachedSelectedStudent,
						name: cachedSelectedStudent.name
					}
				]);
			} else {
				setFilteredStudents(students);
			}
		} else {
			setFilteredStudents(students);
		}
	}, [students, debouncedSearchQuery, selectedItemId, cachedSelectedStudent]);

	useEffect(() => {
		if (!debouncedSearchQuery) {
			setCachedSelectedStudent(null);
		}
	}, [debouncedSearchQuery]);

	const handleSelectTopic = useCallback(
		(topic: SupportTopic) => {
			if (topic.id?.toString().includes(TECHNICAL)) {
				const ticket = supportTicketsData?.pages
					.flatMap(page => page?.data?.data?.items || [])
					.find(x => TECHNICAL + x?.id?.toString() === topic.id);
				setSelectedTicket(ticket);
			} else {
				const ticket = supportTopics.find(x => x.id === topic?.id);
				if (ticket?.unreadCount && topic?.id) {
					readSupportMessageMutation.mutate(topic.id as string);
				}
			}

			setSelectedTopicId(topic.id as number);
			setIsSupport(true);
			setShowReplyInput(false);
		},
		[supportTicketsData, supportTopics]
	);

	const handleReply = useCallback(() => {
		setShowReplyInput(true);
	}, []);

	const handleSendTicketMessage = useCallback(
		(content: string) => {
			if (!selectedTicket?.id || !content.trim()) return;
			sendTicketMessageMutation.mutate({ ticketId: selectedTicket.id, content });
		},
		[selectedTicket?.id, sendTicketMessageMutation]
	);

	const handleSendMessage = useCallback(
		(content: string) => {
			if (!selectedItemId || !content.trim()) return;

			if (isSupport || !!selectedTopicId) {
				const messageRequest: ExpertSupervisorMessageCreateRequest = {
					chatId: selectedTopicId?.toString(),
					message: content
				};
				sendMessageSupportMutation.mutate(messageRequest);
			} else if (selectedParentId) {
				const messageRequest = {
					parentId: selectedParentId,
					studentId: selectedItemId as number,
					message: content
				};
				sendParentMessageMutation.mutate(messageRequest);
			} else {
				const messageRequest: StudentExpertChatMessageCreateRequest = {
					studentId: selectedItemId as number,
					message: content
				};
				sendMessageMutation.mutate(messageRequest);
			}
		},
		[isSupport, selectedTopicId, selectedParentId, selectedItemId, sendMessageSupportMutation, sendParentMessageMutation, sendMessageMutation]
	);

	const handleGoBackPage = useCallback(() => {
		if (window.history.state && window.history.state.idx > 0) {
			navigate(-1);
		} else {
			navigate(NavigationPath.dashboard, { replace: true });
		}
	}, [navigate]);

	const handleBackSupport = useCallback(() => {
		if (selectedTicket) {
			setSelectedItemId(TECHNICAL);
			setSelectedTopicId(null);
			setSelectedTicket(null);
		} else {
			setSelectedTopicId(null);
			if (supports?.[0]?.id) {
				setSelectedItemId(supports[0].id);
			}
		}
		setShowReplyInput(false);
	}, [supports, selectedTicket]);

	const getSelectedEntityInfo = useCallback(() => {
		if (!selectedItemId) return null;
		if (isSupport) {
			if (selectedTopicId) {
				if (isTechnicalTopic) {
					return {
						id: selectedTicket?.id,
						name: selectedTicket?.topicName,
						subTitle: dayjs(selectedTicket?.createdAt).format('DD MMMM YYYY'),
						avatar: DhAvatar
					};
				}
				const topic = supportTopics.find(t => t.id === selectedTopicId);
				return topic
					? {
							id: topic.id,
							name: topic.title,
							subTitle: topic.date,
							avatar: '/src/assets/images/logo.png',
							completed: topic?.completed
						}
					: null;
			}

			const support = supports.find(s => s.id === selectedItemId);
			return support
				? {
						id: support.id,
						name: `${support.name} ${support.surname}`,
						subTitle: support.title,
						avatar: support.avatar
					}
				: null;
		}

		const student = students.find(s => s.id === selectedItemId);
		return student
			? {
					id: student.id,
					name: `${student.name} ${student.surname}`,
					subTitle: student.grade,
					avatar: student.avatar
				}
			: null;
	}, [selectedItemId, isSupport, selectedTopicId, students, supports, supportTopics, selectedTicket]);

	const renderContent = () => {
		if (userType === UserType.GOT_IT) {
			const selectedInfo = getSelectedEntityInfo();

			if (selectedTopicId) {
				const selectedInfo = getSelectedEntityInfo();
				return (
					<div className="content got-it">
						<>
							<div className="chat-header support-content">
								{selectedInfo && (
									<>
										<div className="header-info support-header-info">
											<button
												className="back-button"
												onClick={handleBackSupport}>
												<i className="icons-arrow-left"></i>
											</button>
											<div className="info">
												<div className="name">{selectedInfo.name}</div>
												<div className="status">{selectedInfo.subTitle}</div>
											</div>
										</div>

										<div className="chat-header-menu">
											{!isTechnicalTopic && (
												<button
													className={clsx('reply-button', {
														active: !showReplyInput && messages?.find(x => x)
													})}
													onClick={!showReplyInput ? handleReply : undefined}>
													<i className="icons-reply"></i>
													Yanıtla
												</button>
											)}
										</div>
									</>
								)}
							</div>

							<MessageContentSupport
								messages={messages}
								currentUser={currentUser}
								onLoadMore={handleLoadMoreSupports}
								hasMore={!!hasNextPageSupports}
								isLoading={isFetchingNextPageSupports}
								onReply={!showReplyInput ? handleReply : undefined}
							/>

							{isTechnicalTopic ? (
								<MessageInput onSendMessage={handleSendTicketMessage} />
							) : (
								showReplyInput && <MessageInput onSendMessage={handleSendMessage} />
							)}
						</>
					</div>
				);
			}

			return (
				<div className="content got-it">
					<div className="mew-topic">
						{isMobile && (
							<button
								className="back-button"
								onClick={handleGoBackPage}>
								<i className="icons-arrow-left"></i>
							</button>
						)}

						<div className="topic-icon">
							<img
								src={DopingIcon}
								alt="Doping Hafıza"
							/>
						</div>

						<div className="topic-info">
							<div className="topic-title">Doping Hafıza</div>
							<div className="topic-description">Teknik Destek</div>
						</div>

						<button
							className="new-topic-button"
							onClick={() => setShowNewTicketModal(true)}>
							<i className="icons-add-circle"></i>
							Yeni Konu
						</button>
					</div>
					<TopicListNormal
						topics={supportTopics}
						onSelectTopic={handleSelectTopic}
						currentUser={selectedInfo}
						onNewTopic={() => (selectedItemId === TECHNICAL ? setShowNewTicketModal(true) : setShowNewTopicModal(true))}
						fetchNextData={fetchNextSupportTopicData}
						hasNextPage={hasNextPageSupportTopicData}
						isLoading={isFetchingNextPageSupportTopicData}
						showHeader={false}
						showFilter={isTechnical}
						hasRelatedStudent={hasRelatedStudent}
						onFilterChange={setHasRelatedStudent}
					/>
				</div>
			);
		}

		if (selectedItemId) {
			const selectedInfo = getSelectedEntityInfo();
			if (isSupport) {
				return (
					<div className="content">
						{selectedTopicId ? (
							<>
								<div className="chat-header support-content">
									{selectedInfo && (
										<>
											<div className="header-info">
												<button
													className="back-button"
													onClick={handleBackSupport}>
													<i className="icons-arrow-left"></i>
												</button>
												<div className="info">
													<div className="name">{selectedInfo.name}</div>
													<div className="status">{selectedInfo.subTitle}</div>
												</div>
											</div>

											<div className="chat-header-menu">
												{!isTechnicalTopic && !selectedInfo?.completed && (
													<button
														className={clsx('reply-button', {
															active: !showReplyInput && messages?.find(x => x)
														})}
														onClick={!showReplyInput ? handleReply : undefined}>
														<i className="icons-reply"></i>
														Yanıtla
													</button>
												)}
											</div>
										</>
									)}
								</div>

								<MessageContentSupport
									messages={messages}
									currentUser={currentUser}
									onLoadMore={handleLoadMoreSupports}
									hasMore={!!hasNextPageSupports}
									isLoading={isFetchingNextPageSupports}
									onReply={!showReplyInput ? handleReply : undefined}
								/>

								{isTechnicalTopic ? (
									<MessageInput onSendMessage={handleSendTicketMessage} isSupport={true} />
								) : (
									showReplyInput && <MessageInput onSendMessage={handleSendMessage} isSupport={true} />
								)}
							</>
						) : (
							<TopicListNormal
								topics={supportTopics}
								onSelectTopic={handleSelectTopic}
								currentUser={selectedInfo}
								onNewTopic={() => (selectedItemId === TECHNICAL ? setShowNewTicketModal(true) : setShowNewTopicModal(true))}
								fetchNextData={fetchNextSupportTopicData}
								hasNextPage={hasNextPageSupportTopicData}
								isLoading={isFetchingNextPageSupportTopicData}
								onGoBack={() => setSelectedItemId(null)}
								showFilter={isTechnical}
								hasRelatedStudent={hasRelatedStudent}
								onFilterChange={setHasRelatedStudent}
							/>
						)}
					</div>
				);
			}

			return (
				<div className="content">
					{isMobile && selectedInfo && (
						<>
							<div className="mobile-header-title">
								<button
									className="back-button"
									onClick={() => setSelectedItemId(null)}>
									<i className="icons-arrow-left"></i>
								</button>
								<div className="title">Mesajlar</div>
							</div>
							<div className="mobile-message-tabs">
								<div
									className={clsx('tab-item', { selected: !selectedParentId && !showParentPlaceholder })}
									onClick={() => {
										setSelectedParentId(null);
										setShowParentPlaceholder(false);
									}}>
									Öğrenci
								</div>
								{parentChatEnabled && (
									<div
										className={clsx('tab-item', { selected: !!selectedParentId || showParentPlaceholder })}
										onClick={() => {
											if (parentsData && parentsData.length >= 1) {
												setShowMobileParentModal(true);
											} else {
												setShowParentPlaceholder(true);
											}
										}}>
										Veli
										{(parentsData && parentsData?.reduce((acc, curr) => acc + (curr.unreadMessageCount || 0), 0)) > 0 && (
											<div className="unread-badge">{parentsData.reduce((acc, curr) => acc + (curr.unreadMessageCount || 0), 0)}</div>
										)}
										{parentsData && parentsData.length >= 1 && <i className="icons-alt-arrow-down" />}
									</div>
								)}
							</div>
							<div className="mobile-chat-header">
								<div className="mobile-header-user-info">
									{selectedParentId && selectedParent ? (
										<>
											<div className="parent-avatar-mobile">
												{selectedParent?.parentFullName
													?.split(' ')
													?.map((n: string) => n?.[0]?.toUpperCase())
													?.join('')}
											</div>
											<div className="user-info">
												<div className="user-name">{selectedParent?.parentFullName}</div>
												<div className="user-role">{selectedParent?.parentType === 'PARENT' ? 'Veli' : 'Öğretmen'}</div>
											</div>
										</>
									) : (
										<>
											{selectedInfo?.avatar && (
												<Avatar
													src={selectedInfo?.avatar}
													alt={selectedInfo.name as string}
												/>
											)}
											<div className="user-info">
												<div className="user-name">{selectedInfo?.name}</div>
												<div className="user-role">{selectedInfo?.subTitle}</div>
											</div>
										</>
									)}
								</div>

								{!selectedParentId ? (
									<div className="parent-select-button">
										<span>Öğrenci</span>
									</div>
								) : (
									<div className="parent-select-button">{selectedParent?.parentType === 'PARENT' ? 'Veli' : 'Öğretmen'}</div>
								)}
							</div>
						</>
					)}

					<div
						className="chat-header"
						style={{ display: isMobile ? 'none' : 'flex' }}>
						{selectedInfo && (
							<div className="header-info">
								{isMobile && (
									<button
										className="back-button"
										onClick={() => setSelectedItemId(null)}>
										<i className="icons-arrow-left"></i>
									</button>
								)}
								<div className="message-tabs">
									<div
										className={`student-item ${!selectedParentId && !showParentPlaceholder ? 'selected' : ''}`}
										onClick={() => {
											setSelectedParentId(null);
											setShowParentPlaceholder(false);
										}}>
										{selectedInfo?.avatar && (
											<Avatar
												src={selectedInfo?.avatar}
												alt={selectedInfo?.name as string}
											/>
										)}
										<div className="info">
											<div className="name">{selectedInfo?.name}</div>
											<div className="status">{selectedInfo?.subTitle}</div>
										</div>

										<div className="tab-user-type">Öğrenci</div>
									</div>

									{parentChatEnabled &&
										(parentsData && parentsData?.length > 0 ? (
											<div
												className={`parent-item ${selectedParentId ? 'selected' : ''}`}
												onClick={e => {
													e.stopPropagation();
													if (parentsData?.length >= 1 && !selectedParentId && !selectedParent) {
														const singleParent = parentsData[0];
														setSelectedParent(singleParent);
														setSelectedParentId(singleParent?.parentId);
													} else if (selectedParent) {
														setSelectedParentId(selectedParent?.parentId);
													}
												}}>
												{selectedParent?.parentFullName ? (
													<div className="parent-avatar">
														{selectedParent?.parentFullName
															?.split(' ')
															?.map((n: string) => n?.[0]?.toUpperCase())
															?.join('')}
													</div>
												) : parentsData?.length === 1 ? (
													<div className="parent-avatar">
														{parentsData?.[0]?.parentFullName
															?.split(' ')
															?.map((n: string) => n?.[0]?.toUpperCase())
															?.join('')}
													</div>
												) : (
													<div className="parent-avatar">
														{parentsData?.[0]?.parentFullName
															?.split(' ')
															?.map((n: string) => n?.[0]?.toUpperCase())
															?.join('')}
													</div>
												)}
												<div className="info">
													<div className="name">
														{selectedParent?.parentFullName || (parentsData?.length >= 1 ? parentsData?.[0]?.parentFullName : selectedInfo?.name)}
														{selectedParent?.unreadMessageCount ? (
															<div className="unread-count">{selectedParent?.unreadMessageCount}</div>
														) : parentsData?.[0]?.unreadMessageCount ? (
															<div className="unread-count">{parentsData?.[0]?.unreadMessageCount}</div>
														) : null}
													</div>
												</div>

												<div className="tab-user-type">
													{selectedParent?.parentType === 'PARENT'
														? 'Veli'
														: selectedParent?.parentType === 'TEACHER'
															? 'Öğretmen'
															: parentsData?.length === 1
																? parentsData?.[0]?.parentType === 'PARENT'
																	? 'Veli'
																	: 'Öğretmen'
																: 'Veli'}
												</div>
												{parentsData?.length > 1 && (
													<div
														className="tab-change-parent"
														onClick={e => {
															e.stopPropagation();
															setShowParentDropdown(!showParentDropdown);
														}}>
														<i className={`icons-alt-arrow-${showParentDropdown ? 'up' : 'down'}`}></i>
														{(parentsData?.reduce((acc, curr) => acc + (curr?.unreadMessageCount || 0), 0) || 0) -
															(selectedParent ? selectedParent?.unreadMessageCount || 0 : parentsData?.[0]?.unreadMessageCount || 0) && (
															<div className="unread-arrow-count"></div>
														)}
													</div>
												)}

												{showParentDropdown && parentsData?.length > 1 && (
													<div
														className="parent-dropdown"
														onClick={e => e.stopPropagation()}>
														<div className="dropdown-content">
															{parentsData?.map(parent => (
																<div
																	key={parent?.parentId}
																	className={`dropdown-item ${selectedParent?.parentId === parent?.parentId ? 'selected' : ''}`}
																	onClick={e => {
																		e.stopPropagation();
																		setSelectedParent(parent);
																		setSelectedParentId(parent?.parentId);
																		setShowParentDropdown(false);
																	}}>
																	<div className="parent-avatar">
																		{(parent?.parentFullName || 'V E')
																			?.split(' ')
																			?.map((n: string) => n?.[0]?.toUpperCase())
																			?.join('')}
																	</div>
																	<div className="info">
																		<div className="name">
																			{parent?.parentFullName}
																			{parent?.unreadMessageCount ? <div className="unread-count">{parent?.unreadMessageCount}</div> : null}
																		</div>
																	</div>

																	<div className="tab-user-type">{parent?.parentType === 'PARENT' ? 'Veli' : 'Öğretmen'}</div>
																</div>
															))}
														</div>
													</div>
												)}
											</div>
										) : (
											<div
												className={clsx('parent-item-placeholder', { selected: showParentPlaceholder })}
												onClick={() => {
													setShowParentPlaceholder(true);
												}}>
												<img
													src={ParentPlaceholderIcon}
													alt="Parent Placeholder"
													className="parent-placeholder-icon"
												/>
												<div className="info">
													<div className="info-title">Eşleşmiş bir veli bulunmamaktadır.</div>
													<div className="info-description">Öğrencinin velisini eşleştirmek için destek olunmalıdır.</div>
												</div>
											</div>
										))}
								</div>
							</div>
						)}
					</div>
					{showParentPlaceholder ? (
						<ParentPlaceholder onClose={() => setShowParentPlaceholder(false)} />
					) : (
						<>
							<MessageContent
								messages={messages}
								currentUser={currentUser}
								onLoadMore={handleLoadMore}
								selectedType={selectedParentId ? 'parent' : 'student'}
								hasMore={selectedParentId ? !!hasNextPageParent : !!hasNextPage}
								isLoading={selectedParentId ? isFetchingNextPageParent : isFetchingNextPage}
							/>
							<MessageInput onSendMessage={handleSendMessage} />
						</>
					)}

					{showMobileParentModal && (
						<div
							className="mobile-parent-modal-overlay"
							onClick={() => setShowMobileParentModal(false)}>
							<div
								className="mobile-parent-modal"
								onClick={e => e.stopPropagation()}>
								<div className="modal-header">
									<h3>Kime Mesaj Göndermek İstiyorsunuz?</h3>
									<button
										className="close-button"
										onClick={() => setShowMobileParentModal(false)}>
										<i className="icons-close"></i>
									</button>
									<div className="modal-subtitle">Mesaj göndermek istediğiniz veliyi seçin.</div>
								</div>

								<div className="parent-list">
									{parentsData?.map(parent => (
										<div
											key={parent?.parentId}
											className={`parent-option ${selectedParentId === parent.parentId ? 'selected' : ''}`}
											onClick={() => {
												setSelectedParent(parent);
												setSelectedParentId(parent?.parentId);
												setShowMobileParentModal(false);
											}}>
											<div className="option-left">
												<div className="parent-avatar-mobile">
													{parent?.parentFullName
														?.split(' ')
														?.map((n: string) => n?.[0]?.toUpperCase())
														?.join('')}
												</div>
												<div className="option-info">
													<div className="option-name">{parent?.parentFullName}</div>
													<div className="option-role">{parent?.parentType === 'PARENT' ? 'Veli' : 'Öğretmen'}</div>
												</div>
											</div>
											{parent?.unreadMessageCount > 0 && <div className="option-unread">{parent?.unreadMessageCount}</div>}
										</div>
									))}
								</div>
							</div>
						</div>
					)}
				</div>
			);
		}

		return (
			<div className="content placeholder main-placeholder">
				<img
					src={MessagesPlaceholder}
					alt="messages-placeholder"
				/>
				<p>Mesajlarınızı görüntülemek için bir kişi seçin</p>
			</div>
		);
	};

	return (
		<div
			className="messages-container"
			style={isMobile && keyboardHeight > 0 ? { paddingBottom: keyboardHeight } : undefined}>
			{(!isMobile || (isMobile && !selectedItemId)) && (
				<div className="messages-header">
					<h2>Mesajlar</h2>
				</div>
			)}
			<div className="messages-content">
				{((isMobile && !selectedItemId) || !isMobile) && (userType === UserType.KY_APPOINTMENT || userType === UserType.KY_INSTANT) ? (
					<ChatList
						supports={supports}
						students={students}
						filteredStudents={filteredStudents}
						selectedItemId={selectedItemId || undefined}
						onSelectItem={handleSelectItem}
						onSearch={handleSearch}
						onLoadMore={handleLoadMoreStudents}
						hasMore={!!hasNextStudentsPage}
						isLoading={isFetchingNextStudentsPage}
						searchQuery={searchQuery}
					/>
				) : null}

				{(!isMobile || (isMobile && selectedItemId)) && renderContent()}
			</div>
			<NewTopicModal
				isOpen={showNewTopicModal}
				onClose={() => setShowNewTopicModal(false)}
			/>
			<NewSupportTicketModal
				isOpen={showNewTicketModal}
				onClose={() => setShowNewTicketModal(false)}
			/>
		</div>
	);
};

export default MessagesScreen;
